import { HumanizeDurationOptions } from "humanize-duration-ts";

import { compareDatesPredicate, DateTime } from "@bps/date-time";
import { ChangeLog, ChangeLogDto } from "@libs/api/types/common-dtos";
import { IEnv } from "@libs/config/config";

import humanizer from "./humanize-duration";

export const humanizeOptionsHourMinute = {
  units: ["h", "m"],
  round: true
} as HumanizeDurationOptions;

export const humanizeOptionsDayHourMinute = {
  units: ["d", "h", "m"],
  round: true
} as HumanizeDurationOptions;

export const getDurationSinceNowHumanized = (timeUtc: DateTime) => {
  const now = DateTime.now();
  const yesterday = now.minus({ days: 1 });

  const diffDuration = now.diff(timeUtc);
  const oneDayDuration = now.diff(yesterday);

  const durationGreaterThanOneDay = diffDuration > oneDayDuration;
  const humanizeOptions = durationGreaterThanOneDay
    ? humanizeOptionsDayHourMinute
    : humanizeOptionsHourMinute;

  return humanizer.humanize(diffDuration.milliseconds, humanizeOptions);
};

export const convertBytes = (bytes: number) => {
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];

  if (bytes === 0) {
    return "n/a";
  }

  const i: number = Math.floor(Math.log(bytes) / Math.log(1024));

  if (i === 0) {
    return `${bytes} ${sizes[i]}`;
  }

  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

export const compareDatesOrUndefinedPredicate = (
  a: string | Date | DateTime | undefined,
  b: string | Date | DateTime | undefined,
  isSortedDescending?: boolean
) => {
  if (!a) {
    return 1;
  }

  if (!b) {
    return -1;
  }

  const dateA = a instanceof DateTime ? a : DateTime.fromJSDate(new Date(a));
  const dateB = b instanceof DateTime ? b : DateTime.fromJSDate(new Date(b));

  return compareDatesPredicate(dateA, dateB, isSortedDescending);
};

export const toTitleCase = (str: string) => {
  return str.replace(/\w\S*/g, txt => {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
};

export const deepMerge = <T extends Object>(...objects: T[]) => {
  return objects.reduce((src, dest) => {
    if (!dest) return src;

    Object.keys(src).forEach(key => {
      const destValue = dest[key];
      const srcValue = src[key];

      if (typeof srcValue === "object") {
        dest[key] = deepMerge(srcValue, destValue);
      } else if (destValue !== undefined) {
        dest[key] = destValue;
      } else {
        dest[key] = srcValue;
      }
    });

    return dest;
  });
};

export const getEnv = (): IEnv => {
  if (/stage\.bpcloud.dev/.test(window.location.href)) {
    return "stage";
  } else if (/uat.\.bpcloud.dev/.test(window.location.href)) {
    return "uat";
  } else if (/bpcloud.io/.test(window.location.href)) {
    return "prod";
  } else {
    return "dev";
  }
};

export const toChangeLog = (dto?: ChangeLogDto) => {
  return {
    ...dto,
    createdDate: toDateTime(dto?.createdDate),
    updatedDate: toDateTime(dto?.updatedDate)
  };
};

export const toChangeLogDto = (changeLog?: ChangeLog) => {
  return {
    ...changeLog,
    createdDate: fromDateTime(changeLog?.createdDate),
    updatedDate: fromDateTime(changeLog?.updatedDate)
  };
};

export const toDateTime = (value?: string) => {
  return value ? DateTime.fromISO(value) : undefined;
};

export const fromDateTime = (value?: DateTime) => {
  return value?.toISO() ?? undefined;
};

export const getHumanizedTimeRelativeToNow = (
  value: DateTime,
  now: DateTime,
  options?: HumanizeDurationOptions
) => {
  const timePassed = now >= value;
  const timePassedHumanized = humanizer.humanize(
    value.diff(now).milliseconds,
    options ?? humanizeOptionsHourMinute
  );

  return timePassed === true
    ? `${timePassedHumanized} ago`
    : `In ${timePassedHumanized}`;
};
